// eslint-disable-next-line folders/match-regex

import {
    createMetaTitle,
    createMetaDescription,
    getCanonicalHreflang,
    createOGTags,
    mergeMeta,
} from '@modules/seo';
import { getPreloadImageLink } from '~/modules/images';
import { createBlogSchema } from '~/modules/seo/schema/blog-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';

import FeaturedSection from '@modules/articles/component/featured-section/FeaturedSection';
import LatestArticles from '@modules/articles/component/latest-articles/LatestArticles';
import FilmsSection from '@modules/articles/component/films-section/FilmsSection';
import { useLoaderData } from '@remix-run/react';
import FeaturedCarousel from '@modules/articles/component/featured-carousel/FeaturedCarousel';
import { json } from '@remix-run/node';
import { fillExtraInfoForArticleDirectory } from '@modules/articles/util/fill-company-admin.server';
import { adRequest } from '@modules/adverts/api';
import type { loader as archizeenRootLoader } from '~/routes/_app.articles';
import {
    FILMS_CATEGORY_LINK,
    LATEST_ARTICLES_PAGE_SIZE,
    LATEST_FILMS_PAGE_SIZE,
    ROOT_CATEGORY_LINK,
} from '@modules/articles/config/constants';
import type { FeaturedCategorySection } from '@modules/articles/type';
import DirectoryHeader from '~/modules/articles/component/directory-header/DirectoryHeader';
import { shuffleArray } from '~/utils/arrayHelper';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import PageSections from '@modules/articles/component/page-layout/PageSections';
import type { APHandle } from '@modules/root';
import { hasFeature } from '@modules/root';
import { mapAdDataToFeatureSlide } from '@modules/articles/util/map-ad-data-to-feature-slide';
import { type LoaderFunctionArgs } from '@remix-run/node';
import { getRouteData } from '~/modules/root/util/get-route-data';
import {
    TOP_ARTICLES_COUNT,
    getArticleDirectory,
} from '~/modules/articles/api/get-article-directory.server';
import { getLatestArticles } from '~/modules/articles/api/get-latest-articles.server';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { getArticleNavigationItems } from '~/modules/articles/api/get-archizeen-common.server';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;
        const title = createMetaTitle(props, [data?.localisation?.Title]);

        const description = createMetaDescription([
            data?.localisation?.Description,
            `Learn about ${data?.localisation?.Title} through our blog of over 200 articles from expert writers and industry professionals.`,
        ]);

        const blogSchema = createBlogSchema(props);
        const ldSchema = createLDSchema([blogSchema]);

        const { rootData, appData } = getRouteData(matches);
        const isMobile = rootData?.device.type === 'mobile';
        const imageConfigProps = appData?.imageConfigProps;
        const bannerImage =
            data?.articleDirectory?.topReadArticles[0]?.image.src;

        const preloadImages = isMobile
            ? getPreloadImageLink(imageConfigProps, undefined, bannerImage)
            : getPreloadImageLink(imageConfigProps, bannerImage);

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            ...getCanonicalHreflang(props),
            ...ldSchema,
            ...preloadImages,
        ];
    }
);

export const handle: APHandle = {
    isCategoryPage: true,
};

const SHOW_FEATURE_CAROUSEL: string[] = [];

export const loader = async (args: LoaderFunctionArgs) => {
    const { context, request } = args;

    const requestUrl = request.url;

    const [
        user,
        articleDirectory,
        latestArticles,
        latestFilms,
        localisation,
        bannerAds,
        navItems,
    ] = await Promise.all([
        context.getUser(),
        getArticleDirectory(context),
        getLatestArticles({
            ...context,
            categoryLink: ROOT_CATEGORY_LINK,
            limit: LATEST_ARTICLES_PAGE_SIZE,
            offset: 0,
        }),
        getLatestArticles({
            ...context,
            categoryLink: FILMS_CATEGORY_LINK,
            limit: LATEST_FILMS_PAGE_SIZE,
            offset: 0,
        }),
        getPageLocalisation(args),
        adRequest({
            type: 'ArchizeenBannerPlacement',
            requestUrl,
            ...args.context,
        }),
        getArticleNavigationItems(context),
    ]);

    fillExtraInfoForArticleDirectory(
        user,
        articleDirectory,
        latestArticles.articles,
        latestFilms.articles
    );

    const bannerAdsShuffled = bannerAds ? shuffleArray(bannerAds) : [];
    const featuredSlides = bannerAdsShuffled?.length
        ? bannerAdsShuffled
              .slice(0, TOP_ARTICLES_COUNT)
              .map(mapAdDataToFeatureSlide)
        : articleDirectory?.topReadArticles ?? [];

    const filmsFeatureEnabled = hasFeature(user.FeatureFlags, 'archizeenfilms');

    return json({
        deviceType: context.device.type,
        articleDirectory,
        latestArticles,
        latestFilms,
        localisation,
        bannerAds: bannerAdsShuffled,
        navItems,
        filmsFeatureEnabled,
        featuredSlides,
    });
};

const ArchizeenDirectoryPage = () => {
    const {
        articleDirectory,
        latestArticles,
        latestFilms,
        navItems,
        filmsFeatureEnabled,
        featuredSlides,
    } = useLoaderData<typeof loader>();

    const { common } = useLoaderData<typeof archizeenRootLoader>();
    // Keep the first value returned by loader to avoid changing when loader revalidate
    const featuredSlidesMemoized = useLocationMemo(featuredSlides);

    if (!articleDirectory) return null;

    const { featuredSections } = articleDirectory;
    const { articles: films, totalCount } = latestFilms;

    const loadMoreFilms =
        films.length < totalCount ? FILMS_CATEGORY_LINK : undefined;

    return (
        <>
            <DirectoryHeader
                navItems={navItems ?? []}
                content={common?.contentTop}
            />
            <PageSections>
                <FeaturedCarousel
                    featuredSlides={featuredSlidesMemoized}
                    imageLazyLoading={'eager'}
                />
                <LatestArticles
                    title={'The Latest'}
                    latestArticles={latestArticles.articles}
                />
                {featuredSections.map((section) => (
                    <FeaturedSection
                        key={section.link}
                        featuredSection={section as FeaturedCategorySection}
                        showFeaturedSlides={SHOW_FEATURE_CAROUSEL.includes(
                            section.title.toLowerCase()
                        )}
                    />
                ))}
                {filmsFeatureEnabled && latestFilms.articles.length > 0 && (
                    <FilmsSection
                        latestArticles={latestFilms.articles}
                        loadMoreLink={loadMoreFilms}
                    />
                )}
            </PageSections>
        </>
    );
};

export default ArchizeenDirectoryPage;
